"use client";

import { useEffect, useRef, useState } from "react";
import { LOCALE_NAMES, LOCALE_TO_SLUG, type LocaleCode } from "@/lib/labels/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { persistLocaleChoice, PREFERRED_LOCALE_COOKIE } from "./persistLocaleChoice";

const LOCALE_FLAGS: Record<LocaleCode, string> = {
    en: "🇬🇧",
    es: "🇪🇸",
    "pt-BR": "🇧🇷",
    pt: "🇵🇹",
    bg: "🇧🇬",
    el: "🇬🇷",
    de: "🇩🇪",
    fr: "🇫🇷",
    it: "🇮🇹",
    ro: "🇷🇴",
    th: "🇹🇭",
    tr: "🇹🇷",
    vi: "🇻🇳",
    zh: "🇨🇳",
    ko: "🇰🇷",
    da: "🇩🇰",
    cs: "🇨🇿",
    sq: "🇦🇱",
    nl: "🇳🇱",
};

interface LocaleSelectorOverlayProps {
    enabledLocales: LocaleCode[];
    currentPath: string;
}

export function LocaleSelectorOverlay({ enabledLocales, currentPath }: LocaleSelectorOverlayProps) {
    const labels = useLabels();
    const firstButtonRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    // Decide visibility client-side: show only when no preferred-locale cookie is present.
    useEffect(() => {
        const hasCookie = new RegExp(`(?:^|; )${PREFERRED_LOCALE_COOKIE}=`).test(document.cookie);
        if (!hasCookie) setIsOpen(true);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        firstButtonRef.current?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        function handleKey(e: KeyboardEvent) {
            if (e.key !== "Tab") return;
            const dialog = dialogRef.current;
            if (!dialog) return;

            const focusables = dialog.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [isOpen]);

    if (!isOpen) return null;

    function pick(locale: LocaleCode) {
        persistLocaleChoice(locale);
        const slug = LOCALE_TO_SLUG[locale];
        const target = `/${slug}${currentPath === "/" ? "" : currentPath}`;
        window.location.href = target;
    }

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="locale-prompt-title"
            className="locale-prompt-overlay fixed inset-0 z-[100] items-center justify-center bg-black/90 p-4"
        >
            <div
                className="w-full max-w-md rounded-[var(--radius)] bg-card text-card-foreground shadow-[var(--shadow-medium)] border border-border p-6"
            >
                <h2 id="locale-prompt-title" className="text-xl font-semibold mb-5 text-center">
                    {labels.localeSelector.title}
                </h2>
                <ul className="flex flex-col gap-2">
                    {enabledLocales.map((locale, index) => (
                        <li key={locale}>
                            <button
                                ref={index === 0 ? firstButtonRef : undefined}
                                type="button"
                                onClick={() => pick(locale)}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-[var(--radius-small)] border border-border hover:bg-muted transition text-left cursor-pointer"
                            >
                                <span className="text-xl" aria-hidden>
                                    {LOCALE_FLAGS[locale]}
                                </span>
                                <span className="text-base">{LOCALE_NAMES[locale]}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
